import type {
  ReactNode
} from 'react'
import {
  formatScopeClusterList
} from '../../utils/scopeClusters'
import {
  AlertTriangle
} from 'lucide-react'

interface PartialGrantNoticeProps {
  // The clusters we asked Google for that came back ungranted. Empty when the
  // return trip carried no cluster list (older links, or a direct visit), in
  // which case we fall back to naming nothing rather than guessing.
  incompleteClusters: string[]
  // Optional trailing content — the v1 step tucks its privacy/terms links here.
  footer?: ReactNode
}

// Shown when a user came back from Google's consent screen having approved only
// part of it. Naming the box they left unchecked is the whole point: the
// generic "approve the full set" version restarted the step without telling
// them which permission was actually missing.
export function PartialGrantNotice({
  incompleteClusters,
  footer,
}: PartialGrantNoticeProps): JSX.Element {
  const label = formatScopeClusterList(incompleteClusters)
  const isPlural = incompleteClusters.length > 1

  const title = label
    ? `${label} ${isPlural ? 'are' : 'is'} still missing`
    : 'Catch needs all of the required permissions'

  // Deliberately not "you left it unchecked" — Google can drop a scope without
  // the user touching anything (incremental-auth quirks, a Workspace admin
  // policy), so the copy states what came back, not what they did.
  const body = label
    ? `${isPlural ? 'Those weren’t' : 'That wasn’t'} granted last time. Catch needs ${isPlural ? 'them' : 'it'} to do the job — check ${isPlural ? 'them' : 'it'} on Google’s screen and you’re set.`
    : 'Some of the permissions weren’t granted last time. Catch needs the required boxes checked to do the job.'

  return (
    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="font-semibold text-amber-800">{title}</p>
          <p className="text-sm text-amber-700 mt-1">{body}</p>
          {footer}
        </div>
      </div>
    </div>
  )
}
